import { useId, useState } from "react";

/** Something a configuration fits. Mirrors `FitTag` in the API contract, which re-validates on save. */
export interface FitTagValue {
  ref: string;
  name?: string;
}

export const MAX_FIT_TAGS = 10;
const GENERIC_REF = /^[a-z0-9][a-z0-9.-]{0,31}$/;

export type FitTagError = "invalid" | "max" | "duplicate" | "empty";

/** Normalise and validate a new tag against the existing ones and the brand's optional stricter pattern. */
export function addFitTag(existing: FitTagValue[], rawRef: string, rawName: string, pattern?: string): { tags: FitTagValue[] } | { error: FitTagError } {
  const ref = rawRef.trim().toLowerCase().replace(/\s+/g, "");
  if (!ref) return { error: "empty" };
  if (!GENERIC_REF.test(ref) || (pattern && !new RegExp(pattern, "i").test(ref))) return { error: "invalid" };
  if (existing.some((t) => t.ref === ref)) return { error: "duplicate" };
  if (existing.length >= MAX_FIT_TAGS) return { error: "max" };
  const name = rawName.trim().slice(0, 80);
  return { tags: [...existing, name ? { ref, name } : { ref }] };
}

export interface FitTagInputProps {
  value: FitTagValue[];
  onChange: (tags: FitTagValue[]) => void;
  /** Noun for one tag, e.g. "LEGO® set". */
  label: string;
  pattern?: string;
  refPlaceholder?: string;
  namePlaceholder?: string;
  addLabel?: string;
  /** Accessible name of the optional name/description input (defaults to the placeholder). */
  nameLabel?: string;
  messages?: Partial<Record<FitTagError, string>>;
  disabled?: boolean;
}

export function FitTagInput({ value, onChange, label, pattern, refPlaceholder, namePlaceholder, addLabel = "Add", nameLabel, messages = {}, disabled }: FitTagInputProps) {
  const id = useId();
  const [ref, setRef] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<FitTagError | null>(null);
  const add = () => {
    const r = addFitTag(value, ref, name, pattern);
    if ("error" in r) {
      setError(r.error === "duplicate" || r.error === "empty" ? null : r.error);
      return;
    }
    onChange(r.tags);
    setRef("");
    setName("");
    setError(null);
  };
  return (
    // The error belongs to the reference input only (`.is-invalid` + aria-invalid there); the wrapper just gets `.has-error`.
    <div className={`cfg-field cfg-fits${error ? " has-error" : ""}`}>
      <label className="cfg-label" htmlFor={id}>
        <span>{label}</span>
      </label>
      {value.length > 0 && (
        <ul className="cfg-fit-list">
          {value.map((t) => (
            <li key={t.ref} className="cfg-fit-chip">
              <strong>{t.ref}</strong>
              {t.name && <span>{t.name}</span>}
              <button type="button" className="cfg-fit-remove" aria-label={`Remove ${t.ref}`} disabled={disabled} onClick={() => onChange(value.filter((o) => o.ref !== t.ref))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="cfg-fit-row">
        <input
          id={id}
          className={`cfg-input cfg-fit-ref${error ? " is-invalid" : ""}`}
          placeholder={refPlaceholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={ref}
          disabled={disabled}
          onChange={(e) => {
            setRef(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <input
          className="cfg-input cfg-fit-name"
          placeholder={namePlaceholder}
          aria-label={nameLabel ?? namePlaceholder ?? "Name"}
          value={name}
          maxLength={80}
          disabled={disabled}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="cfg-chip" disabled={disabled || !ref.trim()} onClick={add}>
          {addLabel}
        </button>
      </div>
      {error && (
        <p className="cfg-error" id={`${id}-error`}>
          {messages[error] ?? error}
        </p>
      )}
    </div>
  );
}
